/**
 * SEO/AEO/GEO helpers — Prompt 9.
 *
 * - PUBLIC_DOMAIN is hard-coded to production. In preview the canonical still
 *   points at the real domain so crawlers see one canonical URL no matter
 *   which environment they hit. This is the recommended setup for SPAs.
 */
import { CAR_BASES, ROUTE_BASES, GUIDE_BASES, ROUTE_MAP, ROUTE_KEYS, LANGS } from "@/i18n/routeMap";

export const PUBLIC_DOMAIN = "https://spainluxuryrentacar.com";

/** Article + TouristTrip JSON-LD for route detail pages (Prompt 10). */
export function buildRouteJsonLd(route, lang) {
  const title = route?.h1?.[lang] || route?.title?.[lang] || route?.title?.en || "";
  const desc = route?.shortDescription?.[lang] || route?.shortDescription?.en || "";
  const heroUrl = route?.routeMedia?.find((m) => m.isMain)?.url || route?.routeMedia?.[0]?.url;
  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: title,
    description: desc,
    image: heroUrl ? (heroUrl.startsWith("http") ? heroUrl : PUBLIC_DOMAIN + heroUrl) : undefined,
    touristType: route?.recommendedCarType?.[lang] || undefined,
    itinerary: (route?.stops?.[lang] || route?.stops?.en || []).map((s, idx) => ({
      "@type": "Place",
      name: s,
      position: idx + 1,
    })),
    provider: {
      "@type": "AutoRental",
      name: "Spain Luxury Rent Car",
      url: PUBLIC_DOMAIN,
    },
  };
}

/** Default SEO defaults per route, used when admin hasn't customised. */
export function defaultRouteSeoTitle(route, lang) {
  const title = route?.title?.[lang] || route?.title?.en || "";
  if (lang === "es") return `${title} | Ruta premium en Costa Blanca`;
  if (lang === "ru") return `${title} | Премиальный маршрут на Коста-Бланке`;
  return `${title} | Luxury route on Costa Blanca`;
}

export function defaultRouteSeoDescription(route, lang) {
  return route?.shortDescription?.[lang] || route?.shortDescription?.en || "";
}

export const SERVICE_AREAS = [
  "Benidorm", "Alicante", "Costa Blanca", "Altea", "Calpe", "Jávea",
  "Dénia", "Finestrat", "San Juan", "Torrevieja", "Valencia", "Murcia",
];

/** Build the alternate URLs for a given page key + slug.
 *  `slugMap` (optional) lets callers pass per-language slugs so translated
 *  guide/route URLs resolve correctly (e.g. ES uses a translated slug). */
export function buildHreflangAlternates({ key, slug, slugMap }) {
  const out = {};
  for (const lang of LANGS) {
    const lgSlug = (slugMap && slugMap[lang]) || slug || "";
    let path;
    if (key === ROUTE_KEYS.CAR_DETAIL && lgSlug) path = `${CAR_BASES[lang]}/${lgSlug}`;
    else if (key === ROUTE_KEYS.ROUTE_DETAIL && lgSlug) path = `${ROUTE_BASES[lang]}/${lgSlug}`;
    else if (key === ROUTE_KEYS.GUIDE_DETAIL && lgSlug) path = `${GUIDE_BASES[lang]}/${lgSlug}`;
    else if (ROUTE_MAP[key]) path = ROUTE_MAP[key][lang];
    else path = ROUTE_MAP[ROUTE_KEYS.HOME][lang];
    out[lang] = PUBLIC_DOMAIN + path;
  }
  return out;
}

/** LocalBusiness / AutoRental JSON-LD — same for every page. */
export function buildLocalBusinessJsonLd(settings) {
  const phone = settings?.websitePhone || "+34 613 207 817";
  return {
    "@context": "https://schema.org",
    "@type": "AutoRental",
    name: "Spain Luxury Rent Car",
    alternateName: "Spain Luxury Rentals",
    url: PUBLIC_DOMAIN,
    telephone: phone,
    email: settings?.email || "spainluxuryrentacar@gmail.com",
    image: settings?.heroFallbackImage || `${PUBLIC_DOMAIN}/og-default.jpg`,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Benidorm",
      addressRegion: "Alicante",
      addressCountry: "ES",
      streetAddress: "Benidorm, Alicante, Costa Blanca",
    },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    }],
    areaServed: SERVICE_AREAS.map((n) => ({ "@type": "City", name: n })),
    sameAs: [],
  };
}

/** Vehicle JSON-LD for the car detail page. */
export function buildVehicleJsonLd(vehicle, lang) {
  const main = vehicle?.images?.find((i) => i.isMain)?.url || vehicle?.images?.[0]?.url;
  const desc = vehicle?.descriptionShort?.[lang] || vehicle?.descriptionShort?.en || "";
  const data = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    name: vehicle.name,
    brand: vehicle.brand ? { "@type": "Brand", name: vehicle.brand } : undefined,
    model: vehicle.model || vehicle.name,
    image: main ? `${PUBLIC_DOMAIN}${main}` : undefined,
    description: desc,
    vehicleTransmission: vehicle.transmission || undefined,
    fuelType: vehicle.fuelType || undefined,
    seatingCapacity: vehicle.seats || undefined,
    vehicleEngine: vehicle.horsepower
      ? { "@type": "EngineSpecification", enginePower: { "@type": "QuantitativeValue", value: vehicle.horsepower, unitText: "HP" } }
      : undefined,
    speed: vehicle.topSpeed
      ? { "@type": "QuantitativeValue", value: vehicle.topSpeed, unitCode: "KMH" }
      : undefined,
    accelerationTime: vehicle.acceleration
      ? { "@type": "QuantitativeValue", value: vehicle.acceleration, unitText: "s 0-100km/h" }
      : undefined,
  };
  if (vehicle.priceMonthly) {
    data.offers = {
      "@type": "Offer",
      price: vehicle.priceMonthly,
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: vehicle.priceMonthly,
        priceCurrency: "EUR",
        unitText: "MONTH",
      },
      availability: vehicle.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      areaServed: SERVICE_AREAS.map((n) => ({ "@type": "City", name: n })),
    };
  }
  // Drop undefined keys
  return JSON.parse(JSON.stringify(data));
}

/** Breadcrumb JSON-LD. */
export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: PUBLIC_DOMAIN + it.path,
    })),
  };
}

/** FAQ JSON-LD. */
export function buildFaqJsonLd(faqs, lang) {
  if (!faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs
      .filter((f) => (f?.q?.[lang] || f?.q?.en) && (f?.a?.[lang] || f?.a?.en))
      .map((f) => ({
        "@type": "Question",
        name: f.q[lang] || f.q.en,
        acceptedAnswer: { "@type": "Answer", text: f.a[lang] || f.a.en },
      })),
  };
}

/** Category copy per Prompt 9 §6 — generic SEO blurbs by category. */
export const CATEGORY_COPY = {
  muscle: {
    en: "American muscle cars are ideal for drivers who want presence, sound and attitude on the Mediterranean coast. Our Dodge Challenger, Dodge Charger and Chevrolet Camaro options are designed for clients looking for a different car rental experience in Benidorm, Alicante and Costa Blanca.",
    es: "Los muscle cars americanos son ideales para conductores que buscan presencia, sonido y actitud en la costa mediterránea. Nuestros Dodge Challenger, Dodge Charger y Chevrolet Camaro están pensados para clientes que quieren una experiencia de alquiler diferente en Benidorm, Alicante y Costa Blanca.",
    ru: "Американские muscle cars подойдут тем, кто ищет мощность, звук и характер на средиземноморском побережье. Dodge Challenger, Dodge Charger и Chevrolet Camaro созданы для клиентов, которым нужен особенный опыт аренды в Бенидорме, Аликанте и на Коста-Бланке.",
  },
  luxury_suv: {
    en: "Luxury SUVs are perfect for families, airport transfers, hotel delivery and premium trips across Costa Blanca. They combine comfort, space, safety and status for clients who want a refined rental experience.",
    es: "Los SUV de lujo son perfectos para familias, traslados desde aeropuerto, entregas en hotel y viajes premium por Costa Blanca. Combinan confort, espacio, seguridad y estatus para clientes que buscan una experiencia de alquiler refinada.",
    ru: "Премиальные SUV идеально подходят для семей, трансферов из аэропорта, доставки в отель и комфортных поездок по Коста-Бланке. Они сочетают комфорт, пространство, безопасность и статус.",
  },
  performance_gt: {
    en: "Performance GT cars are made for clients who want speed, design and exclusivity without giving up comfort. They are ideal for coastal routes, business trips and premium weekends in Alicante and Costa Blanca.",
    es: "Los gran turismo performance están pensados para clientes que quieren velocidad, diseño y exclusividad sin renunciar al confort. Son ideales para rutas costeras, viajes de negocio y fines de semana premium en Alicante y Costa Blanca.",
    ru: "Performance GT подойдут клиентам, которым нужны скорость, дизайн и эксклюзивность без потери комфорта. Они идеальны для прибрежных маршрутов, деловых поездок и премиальных выходных в Аликанте и на Коста-Бланке.",
  },
};

/** Brand availability disclaimer (Prompt 9 §1). */
export const BRAND_DISCLAIMER = {
  en: "If you are looking for Ferrari, Lamborghini, BMW M, Mercedes G-Class, Jeep Wagoneer or similar luxury cars in Costa Blanca, contact us to check current availability and alternatives.",
  es: "Si buscas Ferrari, Lamborghini, BMW M, Mercedes Clase G, Jeep Wagoneer o coches de lujo similares en Costa Blanca, contacta con nosotros para consultar disponibilidad actual y alternativas.",
  ru: "Если вы ищете Ferrari, Lamborghini, BMW M, Mercedes G-Class, Jeep Wagoneer или похожие автомобили премиум-класса на Коста-Бланке, свяжитесь с нами, чтобы уточнить актуальную доступность и альтернативы.",
};

/** Auto alt-text generator (Prompt 9 §7). */
export function altForVehicleImage(vehicleName, lang) {
  const templates = {
    es: "Alquiler de {name} en Benidorm, Costa Blanca",
    en: "{name} luxury car rental in Benidorm, Costa Blanca",
    ru: "Аренда {name} в Бенидорме, Коста-Бланка",
    fr: "Location de {name} à Benidorm, Costa Blanca",
    de: "{name} Luxuswagen mieten in Benidorm, Costa Blanca",
    it: "Noleggio {name} a Benidorm, Costa Blanca",
    nl: "{name} luxeauto huren in Benidorm, Costa Blanca",
    pl: "Wynajem {name} w Benidormie, Costa Blanca",
  };
  return (templates[lang] || templates.en).replace("{name}", vehicleName);
}

/** Default SEO title/description per car (used when admin hasn't customised). */
export function defaultSeoTitle(vehicle, lang) {
  const n = vehicle.name;
  if (lang === "es") return `Alquiler ${n} en Benidorm | ${vehicle.brand || "Lujo"} Costa Blanca`;
  if (lang === "ru") return `Аренда ${n} в Бенидорме | ${vehicle.brand || "Премиум"} Коста-Бланка`;
  return `${n} rental in Benidorm | ${vehicle.brand || "Luxury"} Costa Blanca`;
}

export function defaultSeoDescription(vehicle, lang) {
  const n = vehicle.name;
  if (lang === "es") return `Alquila un ${n} en Benidorm y Costa Blanca. Servicio premium, reserva por WhatsApp y opciones de entrega.`;
  if (lang === "ru") return `Арендуйте ${n} в Бенидорме и на Коста-Бланке. Премиальный сервис и бронирование через WhatsApp.`;
  return `Rent a ${n} in Benidorm and Costa Blanca. Premium service, WhatsApp booking and delivery options.`;
}
